"use client";

import { useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { createContext, useContext, useEffect, useState } from "react";
import { fetcher } from "@/lib/http/fetcher";
import type { IPaginatedResult } from "@/lib/shared/types/pagination";
import { giftSetGridKeys } from "../shared/gridKeys";
import type { GiftSet } from "../shared/types";

const GiftSetsContext = createContext<GiftSet[]>([]);

function useGiftSetsQuery(initialGiftSets: GiftSet[]) {
  const locale = useLocale();

  return useQuery<IPaginatedResult<GiftSet>>({
    queryKey: giftSetGridKeys.list(locale, 1),
    queryFn: () => {
      const params = new URLSearchParams({
        locale,
        page: "1",
      });
      return fetcher(`/${locale}/api/gift-sets?${params.toString()}`);
    },
    initialData:
      initialGiftSets.length > 0
        ? {
            docs: initialGiftSets,
            hasNextPage: false,
            hasPrevPage: false,
            limit: initialGiftSets.length,
            page: 1,
            pagingCounter: 1,
            totalDocs: initialGiftSets.length,
            totalPages: 1,
          }
        : undefined,
    staleTime: 5 * 60 * 1000,
  });
}

function HydratedGiftSetsProvider({
  value,
  children,
}: {
  value: GiftSet[];
  children: React.ReactNode;
}) {
  const { data } = useGiftSetsQuery(value);
  return (
    <GiftSetsContext.Provider value={data?.docs ?? value}>{children}</GiftSetsContext.Provider>
  );
}

export function GiftSetsProvider({
  value,
  children,
}: {
  value: GiftSet[];
  children: React.ReactNode;
}) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return <GiftSetsContext.Provider value={value}>{children}</GiftSetsContext.Provider>;
  }

  return <HydratedGiftSetsProvider value={value}>{children}</HydratedGiftSetsProvider>;
}

export function useGiftSets(): GiftSet[] {
  return useContext(GiftSetsContext);
}
